import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  Chip,
  ChipDelete,
  Divider,
} from '@mui/joy';
import { Plus as AddIcon } from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../lib/api';
import type { Organization } from '../../../types';
import { useRegisterStepSave, type RegisterStepSave } from '../useRegisterStepSave';

interface ServicesStepProps {
  registerSave: RegisterStepSave;
}

const PREDEFINED_SERVICES: Record<string, string[]> = {
  'Residential Roofing': [
    'Emergency Services',
    'Residential Roof Replacement',
    'Roof Rejuvenation',
    'Residential Roof Repairs',
    'Residential Roof Maintenance',
    'Residential Roof Installation',
    'Storm and Hail Damage',
  ],
  'Commercial Roofing': [
    'Commercial Roof Replacement',
    'Commercial Roof Repairs',
    'Commercial Roof Installation',
    'Commercial Roof Maintenance',
  ],
};

const allPredefinedServices = Object.values(PREDEFINED_SERVICES).flat();

export const ServicesStep: React.FC<ServicesStepProps> = ({ registerSave }) => {
  const queryClient = useQueryClient();
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [customServiceInput, setCustomServiceInput] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const customServices = selectedServices.filter(
    (s) => !allPredefinedServices.includes(s)
  );

  // Fetch organization data
  const { data: orgData, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await api.get<Organization>('/api/organizations/current/');
      return response.data;
    },
  });

  // Initialize selected services from org data
  useEffect(() => {
    if (orgData?.services) {
      setSelectedServices(orgData.services);
    }
  }, [orgData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (services: string[]) => {
      const response = await api.patch<Organization>('/api/organizations/current/', {
        services,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      setSaveSuccess(true);
      setSaveError(null);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error: any) => {
      console.error('Error saving services:', error);
      setSaveError('Failed to save services. Please try again.');
    },
  });

  const handleSave = async () => {
    await saveMutation.mutateAsync(selectedServices);
  };

  // Also run by the wizard's "Save & Continue" so picking services and using
  // the footer button persists them, not just the in-step button.
  useRegisterStepSave(registerSave, handleSave);

  const handleAddCustomService = () => {
    const trimmed = customServiceInput.trim();
    if (trimmed && !selectedServices.includes(trimmed)) {
      setSelectedServices((prev) => [...prev, trimmed]);
    }
    setCustomServiceInput('');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography level="body-md" sx={{ mb: 3, color: 'neutral.600' }}>
        Select the services your company offers. This information helps ApexOS generate
        better marketing content, improve your SEO, and tailor your experience.
      </Typography>

      {saveError && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}
      {saveSuccess && (
        <Alert color="success" sx={{ mb: 2 }}>
          Services saved successfully!
        </Alert>
      )}

      {Object.entries(PREDEFINED_SERVICES).map(([category, services]) => (
        <Box key={category} sx={{ mb: 3 }}>
          <Typography level="title-md" sx={{ mb: 1.5 }}>
            {category}
          </Typography>
          <Grid container spacing={1}>
            {services.map((service) => (
              <Grid xs={12} sm={6} key={service}>
                <Checkbox
                  label={service}
                  checked={selectedServices.includes(service)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedServices((prev) => [...prev, service]);
                    } else {
                      setSelectedServices((prev) =>
                        prev.filter((s) => s !== service)
                      );
                    }
                  }}
                  size="sm"
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />

      <Typography level="title-md" sx={{ mb: 1.5 }}>
        Custom Services
      </Typography>
      <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600' }}>
        Add any additional services not listed above (e.g., Fencing, Siding, Windows,
        Gutters).
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Input
          placeholder="Enter a custom service..."
          value={customServiceInput}
          onChange={(e) => setCustomServiceInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && customServiceInput.trim()) {
              e.preventDefault();
              handleAddCustomService();
            }
          }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          color="primary"
          startDecorator={<AddIcon />}
          disabled={!customServiceInput.trim()}
          onClick={handleAddCustomService}
        >
          Add
        </Button>
      </Stack>
      {customServices.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {customServices.map((service) => (
            <Chip
              key={service}
              variant="soft"
              color="primary"
              endDecorator={
                <ChipDelete
                  onDelete={() =>
                    setSelectedServices((prev) =>
                      prev.filter((s) => s !== service)
                    )
                  }
                />
              }
            >
              {service}
            </Chip>
          ))}
        </Stack>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="solid"
          color="primary"
          onClick={handleSave}
          loading={saveMutation.isPending}
        >
          Save Services
        </Button>
      </Box>
    </Box>
  );
};

export default ServicesStep;
